import { t, useLanguage, formatMoney } from "../../i18n";
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { paymentAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Heart, Coffee, Star, Sparkles, ArrowLeft, Loader2, Apple, Smartphone, CreditCard } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { ExpressTipPayment } from '../../components/payments/ExpressCheckout';
import GoogleReview from '../../components/GoogleReview';
const TIP_OPTIONS = [{
  amount: 1,
  label: '1€',
  icon: Coffee,
  get description() {
    return t("Un café");
  }
}, {
  amount: 2,
  label: '2€',
  icon: Heart,
  get description() {
    return t("Merci !");
  }
}, {
  amount: 5,
  label: '5€',
  icon: Star,
  get description() {
    return t("Super service");
  }
}, {
  amount: 10,
  label: '10€',
  icon: Sparkles,
  get description() {
    return t("Extraordinaire");
  }
}];
export default function TipPage() {
  useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedTip, setSelectedTip] = useState(null);
  const [customTip, setCustomTip] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [reviewStep, setReviewStep] = useState(false);
  const successParam = searchParams.get('success');
  const tipSessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');
  const tableNumber = searchParams.get('table');

  // Check for success redirect
  useEffect(() => {
    if (successParam === 'true') {
      setReviewStep(true);
      if (tipSessionId) paymentAPI.getStatus(tipSessionId).then(({
        data
      }) => {
        setSuccess(data.session_type === 'tip' && data.payment_status === 'paid');
      }).catch(() => {});
    }
  }, [successParam, tipSessionId]);
  const getTipAmount = () => selectedTip || parseFloat(customTip) || 0;
  const handleExpressSuccess = () => {
    setSuccess(true);
  };
  const handleTipPayment = async () => {
    const tipAmount = getTipAmount();
    if (!tipAmount || tipAmount <= 0) {
      toast.error(t("Veuillez sélectionner un montant"));
      return;
    }
    setLoading(true);
    try {
      const response = await paymentAPI.createTipCheckout(orderId, tipAmount, window.location.origin);
      window.location.href = response.data.url;
    } catch (err) {
      toast.error(t("Erreur lors du paiement"));
      setLoading(false);
    }
  };
  const handleSkip = () => {
    setReviewStep(true);
  };
  if (reviewStep || success) return <GoogleReview orderId={orderId} tipped={success} onFinish={() => navigate('/my-orders')} />;
  return <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      {/* Header */}
      <div className="p-4">
        <button aria-label={t("Continuer sans pourboire")} onClick={handleSkip} className="p-2 hover:bg-white/50 rounded-full transition-colors">

          <ArrowLeft className="w-5 h-5" />
        </button>
      </div>

      <div className="px-6 pb-6">
        {/* Hero */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-center mb-8">

          <div className="w-20 h-20 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Heart className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2" style={{
          fontFamily: 'Manrope'
        }}>{t("Satisfait du service ?")}

          </h1>
          <p className="text-gray-500">{t("Laissez un pourboire à notre équipe")}
            {tableNumber ? ` (${t("Table")} ${tableNumber})` : ''}
          </p>
        </motion.div>

        {/* Tip Options */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="grid grid-cols-2 gap-3 mb-6">

          {TIP_OPTIONS.map((option, index) => {
          const Icon = option.icon;
          const isSelected = selectedTip === option.amount;
          return <motion.button key={option.amount} initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.1 + index * 0.05
          }} onClick={() => {
            setSelectedTip(option.amount);
            setCustomTip('');
          }} data-testid={`tip-option-${option.amount}`} className={`
                  p-4 rounded-2xl border-2 transition-all active:scale-95
                  ${isSelected ? 'border-amber-500 bg-amber-50 shadow-md' : 'border-gray-200 bg-white hover:border-amber-300'}
                `}>

                <div className={`
                  w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2
                  ${isSelected ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600'}
                `}>
                  <Icon className="w-6 h-6" />
                </div>
                <p className="text-xl font-bold" style={{
              fontFamily: 'JetBrains Mono'
            }}>
                  {formatMoney(option.amount)}
                </p>
                <p className="text-xs text-gray-500 mt-1">{option.description}</p>
              </motion.button>;
        })}
        </motion.div>

        {/* Custom Amount */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="mb-6">

          <p className="text-sm text-gray-500 text-center mb-2">{t("Ou entrez un montant personnalisé")}</p>
          <div className="relative">
            <input aria-label={t("Montant du pourboire en euros")} min="0.50" max="200" step="0.01" type="number" placeholder={t("Autre montant")} value={customTip} onChange={e => {
            setCustomTip(e.target.value);
            setSelectedTip(null);
          }} className="w-full bg-white border-2 border-gray-200 rounded-xl px-4 py-4 text-center text-lg font-semibold focus:border-amber-500 focus:outline-none transition-colors" style={{
            fontFamily: 'JetBrains Mono'
          }} data-testid="custom-tip-input" />

            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">€</span>
          </div>
        </motion.div>

        {/* Action Buttons */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="space-y-3">

          {/* Express Payment (Apple Pay / Google Pay) */}
          {getTipAmount() > 0 && orderId && <div className="mb-2">
              <ExpressTipPayment orderId={orderId} amount={getTipAmount()} onSuccess={handleExpressSuccess} />

            </div>}

          {/* Divider */}
          {getTipAmount() > 0 && <div className="flex items-center gap-3 text-xs text-gray-400 py-1">
              <div className="flex-1 h-px bg-gray-200"></div>
              <span>{t("ou payer par carte")}</span>
              <div className="flex-1 h-px bg-gray-200"></div>
            </div>}

          <Button onClick={handleTipPayment} disabled={loading || !selectedTip && !customTip} data-testid="send-tip-btn" className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-full py-6 text-lg font-semibold shadow-lg flex items-center justify-center gap-3 active:scale-95 transition-transform disabled:opacity-50">

            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>
                <CreditCard className="w-5 h-5" />{t("Payer par carte")}
              {getTipAmount() > 0 ? formatMoney(getTipAmount()) : ''}
              </>}
          </Button>

          <button onClick={handleSkip} className="w-full py-3 text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors" data-testid="skip-tip-btn">{t("Non merci, peut-être une prochaine fois")}

          </button>
        </motion.div>

        {/* Trust Badge */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.5
      }} className="mt-8 text-center space-y-2">

          <div className="flex items-center justify-center gap-3 text-gray-400">
            <Apple className="w-5 h-5" />
            <Smartphone className="w-5 h-5" />
            <CreditCard className="w-4 h-4" />
          </div>
          <p className="text-xs text-gray-400">{t("100% reversé à l'équipe • Paiement sécurisé par Stripe")}

          </p>
        </motion.div>
      </div>
    </div>;
}
